import { ConfiguredFlow } from 'app/modules/dataSettings/types';
import { RuleStatuses } from 'app/modules/rules/models';

export enum Unit21DataClassifier {
  // CGDOs
  ACTION_EVENT = 'ACTION_EVENT',
  ENTITY = 'ENTITY',
  EVENT = 'EVENT',
  INSTRUMENT = 'INSTRUMENT',
  // Secondaries (sub objects)
  ADDRESS = 'ADDRESS',
  CLIENT_FINGERPRINT = 'CLIENT_FINGERPRINT',
  EMAIL_ADDRESS = 'EMAIL_ADDRESS',
  IP_ADDRESS = 'IP_ADDRESS',
  PHONE_NUMBER = 'PHONE_NUMBER',
  PHYSICAL_ID = 'PHYSICAL_ID',
  // TRM enrichment types
  TRM_RISK_INDICATOR = 'TRM_RISK_INDICATOR',
  TRM_EXPOSURE_PATH = 'TRM_EXPOSURE_PATH',
  TRM_ENTITY = 'TRM_ENTITY',
  TRM_ADDRESS_BEHAVIORAL_SIGNATURE = 'TRM_ADDRESS_BEHAVIORAL_SIGNATURE',
  // U21GDOs
  ALERT = 'ALERT',
  CASE = 'CASE',
  RULE = 'RULE',
  // Misc
  WATCHLIST = 'WATCHLIST',
  ALL = 'ALL',
}

export enum DataSettingFieldType {
  DATE = 'DATE',
  DATE_TIME = 'DATE_TIME',
  NUMBER = 'NUMBER',
  TEXT = 'TEXT',
  LIST = 'LIST',
  ENUM = 'ENUM',
  BOOLEAN = 'BOOLEAN',
  ENTITY_REFERENCE = 'ENTITY_REFERENCE',
  INSTRUMENT_REFERENCE = 'INSTRUMENT_REFERENCE',
  JSON = 'JSON',
  ANY = 'ANY',
}

export type CustomDataRelatedRules = {
  id: number;
  title: string;
  status: RuleStatuses;
};

export enum DataSettingsKeyType {
  NATIVE = 'NATIVE',
  CUSTOM = 'CUSTOM',
}

export enum SemanticTypes {
  ACCOUNT_NUMBER = 'ACCOUNT_NUMBER',
  ACH_SEC_CODE = 'ACH_SEC_CODE',
  ACH_RETURN_CODE = 'ACH_RETURN_CODE',
  CHECK_MEMO = 'CHECK_MEMO',
  CHECK_NUMBER = 'CHECK_NUMBER',
  EMAIL_ADDRESS = 'EMAIL_ADDRESS',
  INSTITUTION_NUMBER = 'INSTITUTION_NUMBER',
  JH_CHECK_IMAGE_NUMBER = 'JH_CHECK_IMAGE_NUMBER',
  MCC_CODE = 'MCC_CODE',
  NACHA_DESCRIPTION = 'NACHA_DESCRIPTION',
  NAICS_CODE = 'NAICS_CODE',
  ROUTING_NUMBER = 'ROUTING_NUMBER',
  TRACE_ID = 'TRACE_ID',
  URL = 'URL',
}

// these are lower-case for compadibility with dmb stuff

export enum TxnEventNativeFields {
  ADDRESS = 'address',
  AMOUNT = 'amount',
  CLIENT_FINGERPRINT = 'client_fingerprint',
  EVENT_ID = 'event_id',
  EVENT_TIME = 'event_time',
  EXCHANGE_RATE = 'exchange_rate',
  EXTERNAL_FEE = 'external_fee',
  FEE = 'fee',
  IP_ADDRESS = 'ip_address',
  RECEIVED_AMOUNT = 'received_amount',
  RECEIVED_CURRENCY = 'received_currency',
  SENT_AMOUNT = 'sent_amount',
  SENT_CURRENCY = 'sent_currency',
  STATUS = 'status',
  TRANSACTION_HASH = 'transaction_hash',
  TYPE = 'type',
  CONVERSION_NOTES = 'conversion_notes',
  SENDER_ENTITY_ID = 'sender_entity_id',
  RECEIVER_ENTITY_ID = 'receiver_entity_id',
  SENDER_INSTRUMENT_ID = 'sender_instrument_id',
  RECEIVER_INSTRUMENT_ID = 'receiver_instrument_id',
  FINGERPRINT_ANDROID_EMULATOR = 'fingerprint_android_emulator',
  FINGERPRINT_BROWSER_TAMPERING = 'fingerprint_browser_tampering',
  FINGERPRINT_CLONED_APP = 'fingerprint_cloned_app',
  FINGERPRINT_CONFIDENCE_SCORE = 'fingerprint_confidence_score',
  FINGERPRINT_DEVELOPER_TOOLS = 'fingerprint_developer_tools',
  FINGERPRINT_FRIDA_DETECTED = 'fingerprint_frida_detected',
  FINGERPRINT_HIGH_ACTIVITY_THRESHOLD = 'fingerprint_high_activity_threshold',
  FINGERPRINT_INCOGNITO_MODE = 'fingerprint_incognito_mode',
  FINGERPRINT_IP_BLOCKLIST = 'fingerprint_ip_blocklist',
  FINGERPRINT_IP_PROXY = 'fingerprint_ip_proxy',
  FINGERPRINT_IP_TOR = 'fingerprint_ip_tor',
  FINGERPRINT_IP_VPN = 'fingerprint_ip_vpn',
  FINGERPRINT_JAILBROKEN = 'fingerprint_jailbroken',
  FINGERPRINT_LOCATION_SPOOFING = 'fingerprint_location_spoofing',
  FINGERPRINT_MALICIOUS_BOT = 'fingerprint_malicious_bot',
  FINGERPRINT_MITM_ATTACK = 'fingerprint_mitm_attack',
  FINGERPRINT_NEWLY_SEEN = 'fingerprint_newly_seen',
  FINGERPRINT_PRIVACY_SETTINGS = 'fingerprint_privacy_settings',
  FINGERPRINT_REMOTE_CONTROL = 'fingerprint_remote_control',
  FINGERPRINT_ROOTED_DEVICE = 'fingerprint_rooted_device',
  FINGERPRINT_TIMEZONE_MISMATCH = 'fingerprint_timezone_mismatch',
  FINGERPRINT_VIRTUAL_MACHINE = 'fingerprint_virtual_machine',
  FINGERPRINT_FACTORY_RESET_TIME = 'fingerprint_factory_reset_time',
  FINGERPRINT_FIRST_SEEN = 'fingerprint_first_seen',
  FINGERPRINT_LAST_SEEN = 'fingerprint_last_seen',
  FINGERPRINT_IP_LATITUDE = 'fingerprint_ip_latitude',
  FINGERPRINT_IP_LONGITUDE = 'fingerprint_ip_longitude',
  FINGERPRINT_DAILY_REQUESTS = 'fingerprint_daily_requests',
  FINGERPRINT_BROWSER_NAME = 'fingerprint_browser_name',
  FINGERPRINT_BROWSER_VERSION = 'fingerprint_browser_version',
  FINGERPRINT_DEVICE_NAME = 'fingerprint_device_name',
  FINGERPRINT_IP_ADDRESS = 'fingerprint_ip_address',
  FINGERPRINT_IP_ASN = 'fingerprint_ip_asn',
  FINGERPRINT_IP_CITY = 'fingerprint_ip_city',
  FINGERPRINT_IP_CONTINENT = 'fingerprint_ip_continent',
  FINGERPRINT_IP_COUNTRY = 'fingerprint_ip_country',
  FINGERPRINT_IP_DATACENTER = 'fingerprint_ip_datacenter',
  FINGERPRINT_IP_TIMEZONE = 'fingerprint_ip_timezone',
  FINGERPRINT_OS_NAME = 'fingerprint_os_name',
  FINGERPRINT_OS_VERSION = 'fingerprint_os_version',
  FINGERPRINT_SUSPECT_SCORE = 'fingerprint_suspect_score',
  FINGERPRINT_BOT = 'fingerprint_bot',
  FINGERPRINT_BOT_TYPE = 'fingerprint_bot_type',
  FINGERPRINT_VISITOR_ID = 'fingerprint_visitor_id',
  FOCSEC_IS_BOT = 'focsec_is_bot',
  FOCSEC_IS_PROXY = 'focsec_is_proxy',
  FOCSEC_IS_TOR = 'focsec_is_tor',
  FOCSEC_IS_VPN = 'focsec_is_vpn',
  GEOLOCATION_CITY = 'geolocation_city',
  GEOLOCATION_COUNTRY = 'geolocation_country',
  GEOLOCATION_LATITUDE = 'geolocation_latitude',
  GEOLOCATION_LONGITUDE = 'geolocation_longitude',
  GEOLOCATION_POSTAL_CODE = 'geolocation_postal_code',
  GEOLOCATION_SUBDIVISION = 'geolocation_subdivision',
}

export enum ActionEventNativeFields {
  ACTION_CONTENT = 'action_content',
  ACTION_TYPE = 'action_type',
  ADDRESS = 'address',
  CLIENT_FINGERPRINT = 'client_fingerprint',
  EVENT_ID = 'event_id',
  EVENT_TIME = 'event_time',
  IP_ADDRESS = 'ip_address',
  STATUS = 'status',
  ENTITY_ID = 'entity_id',
  INTRUMENT_ID = 'instrument_id',
  FINGERPRINT_ANDROID_EMULATOR = 'fingerprint_android_emulator',
  FINGERPRINT_BROWSER_TAMPERING = 'fingerprint_browser_tampering',
  FINGERPRINT_CLONED_APP = 'fingerprint_cloned_app',
  FINGERPRINT_CONFIDENCE_SCORE = 'fingerprint_confidence_score',
  FINGERPRINT_DEVELOPER_TOOLS = 'fingerprint_developer_tools',
  FINGERPRINT_FRIDA_DETECTED = 'fingerprint_frida_detected',
  FINGERPRINT_HIGH_ACTIVITY_THRESHOLD = 'fingerprint_high_activity_threshold',
  FINGERPRINT_INCOGNITO_MODE = 'fingerprint_incognito_mode',
  FINGERPRINT_IP_BLOCKLIST = 'fingerprint_ip_blocklist',
  FINGERPRINT_IP_PROXY = 'fingerprint_ip_proxy',
  FINGERPRINT_IP_TOR = 'fingerprint_ip_tor',
  FINGERPRINT_IP_VPN = 'fingerprint_ip_vpn',
  FINGERPRINT_JAILBROKEN = 'fingerprint_jailbroken',
  FINGERPRINT_LOCATION_SPOOFING = 'fingerprint_location_spoofing',
  FINGERPRINT_MALICIOUS_BOT = 'fingerprint_malicious_bot',
  FINGERPRINT_MITM_ATTACK = 'fingerprint_mitm_attack',
  FINGERPRINT_NEWLY_SEEN = 'fingerprint_newly_seen',
  FINGERPRINT_PRIVACY_SETTINGS = 'fingerprint_privacy_settings',
  FINGERPRINT_REMOTE_CONTROL = 'fingerprint_remote_control',
  FINGERPRINT_ROOTED_DEVICE = 'fingerprint_rooted_device',
  FINGERPRINT_TIMEZONE_MISMATCH = 'fingerprint_timezone_mismatch',
  FINGERPRINT_VIRTUAL_MACHINE = 'fingerprint_virtual_machine',
  FINGERPRINT_FACTORY_RESET_TIME = 'fingerprint_factory_reset_time',
  FINGERPRINT_FIRST_SEEN = 'fingerprint_first_seen',
  FINGERPRINT_LAST_SEEN = 'fingerprint_last_seen',
  FINGERPRINT_IP_LATITUDE = 'fingerprint_ip_latitude',
  FINGERPRINT_IP_LONGITUDE = 'fingerprint_ip_longitude',
  FINGERPRINT_DAILY_REQUESTS = 'fingerprint_daily_requests',
  FINGERPRINT_BROWSER_NAME = 'fingerprint_browser_name',
  FINGERPRINT_BROWSER_VERSION = 'fingerprint_browser_version',
  FINGERPRINT_DEVICE_NAME = 'fingerprint_device_name',
  FINGERPRINT_IP_ADDRESS = 'fingerprint_ip_address',
  FINGERPRINT_IP_ASN = 'fingerprint_ip_asn',
  FINGERPRINT_IP_CITY = 'fingerprint_ip_city',
  FINGERPRINT_IP_CONTINENT = 'fingerprint_ip_continent',
  FINGERPRINT_IP_COUNTRY = 'fingerprint_ip_country',
  FINGERPRINT_IP_DATACENTER = 'fingerprint_ip_datacenter',
  FINGERPRINT_IP_TIMEZONE = 'fingerprint_ip_timezone',
  FINGERPRINT_OS_NAME = 'fingerprint_os_name',
  FINGERPRINT_OS_VERSION = 'fingerprint_os_version',
  FINGERPRINT_SUSPECT_SCORE = 'fingerprint_suspect_score',
  FINGERPRINT_BOT = 'fingerprint_bot',
  FINGERPRINT_BOT_TYPE = 'fingerprint_bot_type',
  FINGERPRINT_VISITOR_ID = 'fingerprint_visitor_id',
  FOCSEC_IS_BOT = 'focsec_is_bot',
  FOCSEC_IS_PROXY = 'focsec_is_proxy',
  FOCSEC_IS_TOR = 'focsec_is_tor',
  FOCSEC_IS_VPN = 'focsec_is_vpn',
  GEOLOCATION_CITY = 'geolocation_city',
  GEOLOCATION_COUNTRY = 'geolocation_country',
  GEOLOCATION_LATITUDE = 'geolocation_latitude',
  GEOLOCATION_LONGITUDE = 'geolocation_longitude',
  GEOLOCATION_POSTAL_CODE = 'geolocation_postal_code',
  GEOLOCATION_SUBDIVISION = 'geolocation_subdivision',
}

export enum EntityNativeFields {
  ACCOUNT_HOLDER_NAME = 'account_holder_name',
  ADDRESSES = 'addresses',
  BUSINESS_NAME = 'business_name',
  CLIENT_FINGERPRINTS = 'client_fingerprints',
  CORPORATE_TAX_ID = 'corporate_tax_id',
  DATE_OF_BIRTH = 'date_of_birth',
  DOING_BUSINESS_AS = 'doing_business_as',
  EMAIL_ADDRESSES = 'email_addresses',
  ENTITY_SUBTYPE = 'entity_subtype',
  ENTITY_TYPE = 'entity_type',
  EXTERNAL_ID = 'external_id',
  FIRST_NAME = 'first_name',
  GENDER = 'gender',
  IP_ADDRESSES = 'ip_addresses',
  LAST_NAME = 'last_name',
  MIDDLE_NAME = 'middle_name',
  PHONE_NUMBERS = 'phone_numbers',
  PHYSICAL_IDS = 'physical_ids',
  REGISTERED_AT = 'registered_at',
  REGISTERED_COUNTRY = 'registered_country',
  REGISTERED_STATE = 'registered_state',
  SSN = 'ssn',
  STATUS = 'status',
  U21_LATEST_RISK_LEVEL = 'u21_latest_risk_level',
  U21_LATEST_RISK_SCORE = 'u21_latest_risk_score',
  U21_LATEST_RISK_SEGMENT = 'u21_latest_risk_segment',
  USER_NAME = 'user_name',
  WEBSITE = 'website',
}

export enum InstrumentNativeFields {
  ADDRESSES = 'addresses',
  CLIENT_FINGERPRINTS = 'client_fingerprints',
  INSTRUMENT_ID = 'instrument_id',
  INSTRUMENT_SUBTYPE = 'instrument_subtype',
  INSTRUMENT_TYPE = 'instrument_type',
  IP_ADDRESSES = 'ip_addresses',
  NAME = 'name',
  REGISTERED_AT = 'registered_at',
  SOURCE = 'source',
  STATUS = 'status',
  FINGERPRINT_FIRST_SEEN = 'fingerprint_first_seen',
  FINGERPRINT_LAST_SEEN = 'fingerprint_last_seen',
}

export enum AddressNativeFields {
  BUILDING_NUMBER = 'building_number',
  UNIT_NUMBER = 'unit_number',
  STREET = 'street',
  CITY = 'city',
  STATE = 'state',
  POSTAL_CODE = 'postal_code',
  COUNTRY = 'country',
  TYPE = 'type',
}

export enum ClientFingerprintNativeFields {
  CREATED_AT = 'created_at',
  CLIENT_FINGERPRINT = 'client_fingerprint',
}

export enum EmailAddressNativeFields {
  CREATED_AT = 'created_at',
  EMAIL_ADDRESS = 'email_address',
}

export enum IpAddressNativeFields {
  AUTONOMOUS_SYSTEM_NUMBER = 'autonomous_system_number',
  AUTONOMOUS_SYSTEM_ORGANIZATION = 'autonomous_system_organization',
  CITY = 'city',
  COUNTRY = 'country',
  COUNTRY_ISO = 'country_iso',
  CREATED_AT = 'created_at',
  FOCSEC_IS_BOT = 'focsec_is_bot',
  FOCSEC_IS_PROXY = 'focsec_is_proxy',
  FOCSEC_IS_TOR = 'focsec_is_tor',
  FOCSEC_IS_VPN = 'focsec_is_vpn',
  IP_ADDRESS = 'ip_address',
  LATITUDE = 'latitude',
  LONGITUDE = 'longitude',
  POSTAL_CODE = 'postal_code',
  SUBDIVISION_0_ISO = 'subdivision_0_iso',
}

export enum PhoneNumberNativeFields {
  CREATED_AT = 'created_at',
  PHONE_NUMBER = 'phone_number',
}

export enum PhysicalIdNativeFields {
  COUNTRY = 'country',
  CREATED_AT = 'created_at',
  EXPIRES_AT = 'expires_at',
  ID_NUMBER = 'id_number',
  REGISTERED_ON = 'registered_on',
  STATE = 'state',
  TYPE = 'type',
}

export enum DataOrigin {
  CUSTOMER_GENERATED = 'CUSTOMER_GENERATED',
  U21_DERIVED = 'U21_DERIVED',
  THIRD_PARTY_ENRICHED = 'THIRD_PARTY_ENRICHED',
}

export enum PerspectiveColumn {
  ENTITY_ID_1 = 'entity_id_1',
  ENTITY_ID_2 = 'entity_id_2',
  ENTITY_ID_3 = 'entity_id_3',
  ENTITY_ID_4 = 'entity_id_4',
  ENTITY_ID_5 = 'entity_id_5',
  ENTITY_ID_6 = 'entity_id_6',
  ENTITY_ID_7 = 'entity_id_7',
  ENTITY_ID_8 = 'entity_id_8',
  ENTITY_ID_9 = 'entity_id_9',
  ENTITY_ID_10 = 'entity_id_10',
  INSTRUMENT_ID_1 = 'instrument_id_1',
  INSTRUMENT_ID_2 = 'instrument_id_2',
  INSTRUMENT_ID_3 = 'instrument_id_3',
  INSTRUMENT_ID_4 = 'instrument_id_4',
  INSTRUMENT_ID_5 = 'instrument_id_5',
  INSTRUMENT_ID_6 = 'instrument_id_6',
  INSTRUMENT_ID_7 = 'instrument_id_7',
  INSTRUMENT_ID_8 = 'instrument_id_8',
  INSTRUMENT_ID_9 = 'instrument_id_9',
  INSTRUMENT_ID_10 = 'instrument_id_10',
}

export type AllNativeFields =
  | ActionEventNativeFields
  | AddressNativeFields
  | ClientFingerprintNativeFields
  | EmailAddressNativeFields
  | EntityNativeFields
  | InstrumentNativeFields
  | IpAddressNativeFields
  | PhoneNumberNativeFields
  | PhysicalIdNativeFields
  | TxnEventNativeFields;

export interface BaseDataSettingsConfigResponse<
  C extends Unit21DataClassifier = Unit21DataClassifier,
> {
  configured_flow: ConfiguredFlow | null;
  data_type: DataSettingFieldType;
  description?: string;
  enum_set: string[];
  exported: boolean;
  ffip_matchable_field_name?: string | null;
  id: number;
  last_customer_update_time: string | null;
  matchable?: boolean;
  recent_values: string[];
  formatted_recent_values: FormattedDataItem[];
  rendering_options?: RenderingOptionsResponse;
  rules?: CustomDataRelatedRules[];
  unit21_data_classifier: C;
  user_facing_label?: string;
  censorship_setting: CensorshipSetting;
  team_ids_with_censorship_read_permission: number[];
  agent_ids_with_censorship_read_permission: number[];
}

export interface CustomDataSettingsConfigResponse extends BaseDataSettingsConfigResponse {
  key_type: DataSettingsKeyType.CUSTOM;
  key_path: string[];
  semantic_type?: SemanticTypes;
  perspective?: PerspectiveColumn;
}

export interface BaseNativeDataSettingsConfigResponse<
  C extends Unit21DataClassifier,
  K extends AllNativeFields,
> extends BaseDataSettingsConfigResponse<C> {
  key_type: DataSettingsKeyType.NATIVE;
  native_field: string;
  native_key: K;
  data_origin: DataOrigin;
  array_type?:
    | Unit21DataClassifier.ADDRESS
    | Unit21DataClassifier.CLIENT_FINGERPRINT
    | Unit21DataClassifier.EMAIL_ADDRESS
    | Unit21DataClassifier.IP_ADDRESS
    | Unit21DataClassifier.PHONE_NUMBER
    | Unit21DataClassifier.PHYSICAL_ID;
}

export type NativeActionEventDataSettingsConfigResponse =
  BaseNativeDataSettingsConfigResponse<
    Unit21DataClassifier.ACTION_EVENT,
    ActionEventNativeFields
  >;
export type NativeTxnEventDataSettingsConfigResponse =
  BaseNativeDataSettingsConfigResponse<
    Unit21DataClassifier.EVENT,
    TxnEventNativeFields
  >;
export type NativeEntityDataSettingsConfigResponse =
  BaseNativeDataSettingsConfigResponse<
    Unit21DataClassifier.ENTITY,
    EntityNativeFields
  >;
export type NativeInstrumentDataSettingsConfigResponse =
  BaseNativeDataSettingsConfigResponse<
    Unit21DataClassifier.INSTRUMENT,
    InstrumentNativeFields
  >;
export type NativeAddressDataSettingsConfigResponse =
  BaseNativeDataSettingsConfigResponse<
    Unit21DataClassifier.ADDRESS,
    AddressNativeFields
  >;
export type NativeClientFingerprintDataSettingsConfigResponse =
  BaseNativeDataSettingsConfigResponse<
    Unit21DataClassifier.CLIENT_FINGERPRINT,
    ClientFingerprintNativeFields
  >;
export type NativeEmailAddressDataSettingsConfigResponse =
  BaseNativeDataSettingsConfigResponse<
    Unit21DataClassifier.EMAIL_ADDRESS,
    EmailAddressNativeFields
  >;
export type NativeIpAddressDataSettingsConfigResponse =
  BaseNativeDataSettingsConfigResponse<
    Unit21DataClassifier.IP_ADDRESS,
    IpAddressNativeFields
  >;
export type NativePhoneNumberDataSettingsConfigResponse =
  BaseNativeDataSettingsConfigResponse<
    Unit21DataClassifier.PHONE_NUMBER,
    PhoneNumberNativeFields
  >;
export type NativePhysicalIdDataSettingsConfigResponse =
  BaseNativeDataSettingsConfigResponse<
    Unit21DataClassifier.PHYSICAL_ID,
    PhysicalIdNativeFields
  >;

export type NativeDataSettingsConfigResponse =
  | NativeActionEventDataSettingsConfigResponse
  | NativeAddressDataSettingsConfigResponse
  | NativeClientFingerprintDataSettingsConfigResponse
  | NativeEmailAddressDataSettingsConfigResponse
  | NativeEntityDataSettingsConfigResponse
  | NativeInstrumentDataSettingsConfigResponse
  | NativeIpAddressDataSettingsConfigResponse
  | NativePhoneNumberDataSettingsConfigResponse
  | NativePhysicalIdDataSettingsConfigResponse
  | NativeTxnEventDataSettingsConfigResponse;

export type OrgDataSettingsConfig =
  | CustomDataSettingsConfigResponse
  | NativeDataSettingsConfigResponse;

export interface OrgDataSettingsConfigResponse {
  data: OrgDataSettingsConfig[];
}

export interface OrgDataSettingsConfigAddEditResponse {
  data: OrgDataSettingsConfig;
}

export type BulkCreateResultStatus = 'created' | 'duplicate' | 'error';

export interface BulkCreateCustomDataSettingsResult {
  key_path: string[];
  status: BulkCreateResultStatus;
  data?: OrgDataSettingsConfig;
  message?: string;
}

export interface BulkCreateCustomDataSettingsResponse {
  results: BulkCreateCustomDataSettingsResult[];
}

export enum FormattedDataCensorshipStatus {
  UNCENSORED = 'UNCENSORED',
  CENSORED = 'CENSORED',
  UNCENSORABLE = 'UNCENSORABLE',
  TOKEN_ENCRYPTED = 'TOKEN_ENCRYPTED',
}

// for use in cgdo response objects' `formatted_data` dict
// `formatted_value` is the value returned after being parsed according to its type in data settings
// e.g. a datetime being returned in ISO-8601 format
export interface FormattedDataItem {
  formatted_value: any;
  is_parseable: boolean;
  raw_value: any;
  censorship: FormattedDataCensorshipStatus;
}

export type FormattedData = Record<number, FormattedDataItem>;

export type FormattedRecentValues = Record<number, FormattedDataItem[]>;

export interface EnumMappingItem {
  description?: string;
  color?: string;
}

export type RenderingOptionsResponse = {
  precision?: number | null;
  timezone?: string | null;
  is_url?: boolean;
  url_prefix?: string;
  url_postfix?: string;
  enum_mapping?: Record<string, EnumMappingItem | undefined>;
  reference_display_fields?: string[];
  // render_in_color?: boolean; Not yet implemented
};

export enum CensorshipSetting {
  ALWAYS_VISIBLE = 'ALWAYS_VISIBLE',
  CENSORED_BUT_VIEWABLE = 'CENSORED_BUT_VIEWABLE',
  CENSORED = 'CENSORED',
  ENCRYPTED = 'ENCRYPTED',
}
